import { Key, useMemo } from 'react'

import { useMemoizedFn } from 'ahooks'
import { Cascader } from 'antd'
import { isEmpty, isNil, last } from 'lodash'

import { DataError } from '@eastcoal/common/components/DataError'
import { findTreePaths, toArray } from '@eastcoal/common/utils/array'
import { pick } from '@eastcoal/common/utils/object'

import { useKindAllNodeStringStore } from '../../Store/hooks/useStore'

import { KindAllNodeStringCascaderCoreProps } from './interface'

export const KindAllNodeStringCascaderCore = ({
  allowClear,
  disabled,
  placeholder,
  showCheckedStrategy = Cascader.SHOW_CHILD
}: KindAllNodeStringCascaderCoreProps) => {
  const { data, error, loading, reload, multiple, valueKey, value, setValue } =
    useKindAllNodeStringStore(state =>
      pick(state, [
        'data',
        'error',
        'loading',
        'reload',
        'multiple',
        'valueKey',
        'value',
        'setValue'
      ])
    )

  const handleChange = useMemoizedFn((keys?: string[] | string[][]) => {
    // 清空
    if (isNil(keys) || keys.length === 0) {
      setValue([])
    }
    // 多选
    else if (multiple) {
      setValue((keys as Key[][]).map(item => `${last(item)}`))
    }
    // 单选
    else {
      setValue([`${last(keys as string[])}`])
    }
  })

  const selectedKeys = useMemo(() => {
    if (isEmpty(data)) {
      return undefined
    }

    if (isEmpty(value)) {
      return undefined
    }

    const values = toArray(value)
    const nodesArr = values.map(key =>
      findTreePaths(data, node => node[valueKey] === key)
    )
    const paths = nodesArr.map(nodes => nodes.map(node => `${node[valueKey]}`))
    const keys = multiple ? paths : paths[0]

    return keys
  }, [data, multiple, value, valueKey])

  return error ? (
    <DataError error={error} loading={loading} reload={reload} />
  ) : (
    <Cascader
      changeOnSelect
      showSearch
      allowClear={allowClear}
      disabled={disabled}
      expandTrigger='hover'
      loading={loading}
      multiple={multiple}
      options={data}
      placeholder={placeholder}
      showCheckedStrategy={showCheckedStrategy}
      value={selectedKeys as any}
      fieldNames={{
        value: valueKey,
        label: 'name',
        children: 'children'
      }}
      onChange={handleChange}
    />
  )
}
